import { useState, useRef } from "react";
import { useI18n } from "react-simple-i18n";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

import { Layout } from "../components/layout/layout";
import { FirstButton } from "../components/buttons/buttons";
import { TextField } from "../components/inputs/textfield";
import { VideoUpload } from "../components/fileupload/videoUpload";
import { GlobalFirstSection, GlobalSpacing } from "../components/layout/styles";
import { Select } from "../components/inputs/select";
import { AddItem, AddItemRefProps } from "../components/inputs/addItem";
import { apiNotification } from "../services/apiNotification";
import { useGlobalContext } from "../provider";
import { ValidateError } from "../utils/customError";
import { restApi } from "../provider/restApi";
import { useCustomNavigate } from "../provider/navigate";
import { languageLists, lectureTypes } from "../config/config";

interface CourseData {
	title: string
	description: string
	price: number
	videoFile: File | null
	videoPreview: string
	lectureType: string
	language: string
	learnOutcomes: string[]

	errorTitle: boolean
	errorDesciption: boolean
	errorPrice: boolean
	errorLectureType: boolean
	errorLanguage: boolean
	errorLecture: boolean
	priceHelperText: string
}

const initCourseData: CourseData = {
	title: "",
	description: "",
	price: 0,
	videoFile: null,
	videoPreview: "",
	lectureType: "",
	language: "en",
	learnOutcomes: [],

	errorTitle: false,
	errorDesciption: false,
	errorPrice: false,
	errorLectureType: false,
	errorLanguage: false,
	errorLecture: false,
	priceHelperText: '',
}

const NewCourse = () => {
	const { t } = useI18n();
	const itemsRef = useRef<AddItemRefProps>(); // Reference to the child component
	const [courseData, setCourseData] = useState<CourseData>(initCourseData);
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();
	const { navigate } = useCustomNavigate();

	const changeCourseData = (newData: Partial<CourseData>) => {
		setCourseData({ ...courseData, ...newData });
	}

	const checkValidate = () => {
		if (!courseData.title) return false;
		if (!courseData.description) return false;
		if (!courseData.price) return false;
		if (!courseData.lectureType) return false;
		if (!courseData.language) return false;
		if (!courseData.videoFile) return false;
		if (courseData.price < globalState.minimumAmount) return false;

		return true;
	}

	const handleVideoUpload = (file: any) => {
		changeCourseData({
			videoFile: file,
			errorLecture: false,
			videoPreview: URL.createObjectURL(file),
		})
	}

	const onChangeInputValue = (key: string) => (e: any) => {
		const { name, value } = e.target;
		changeCourseData({ [name]: value, [key]: false });
	}

	const onChangeAddItem = (learnOutcomes: string[]) => {
		changeCourseData({ learnOutcomes: learnOutcomes })
	}

	const handleSubmit = async () => {
		try {
			if (!itemsRef.current) {
				throw new ValidateError("Try after page reload!");
			}

			const validate = checkValidate();
			const learnOutcomesValidate = itemsRef.current.validateItem();

			if (!validate || !learnOutcomesValidate) {
				let errorPriceMessage = "";
				if (courseData.price < globalState.minimumAmount) {
					errorPriceMessage = `Minimum price is ${globalState.minimumAmount} USD`;
				}

				changeCourseData({
					errorTitle: !courseData.title,
					errorDesciption: !courseData.description,
					errorLectureType: !courseData.lectureType,
					errorLanguage: !courseData.language,
					errorLecture: !courseData.videoFile,
					errorPrice: !courseData.price || !!errorPriceMessage,
					priceHelperText: errorPriceMessage
				})

				return false;
			}

			const formData = new FormData();
			formData.append("title", courseData.title);
			formData.append("description", courseData.description);
			formData.append("price", courseData.price.toString());
			formData.append("lectureType", courseData.lectureType);
			formData.append("language", courseData.language);
			formData.append("learnOutcomes", JSON.stringify(courseData.learnOutcomes));

			if (courseData.videoFile) {
				formData.append("courseVideo", courseData.videoFile);
			}

			dispatch({ loading: true });
			const resData: any = await restApi.createCourse(formData);

			dispatch({ loading: false });
			navigate(`/course/${resData.id}`);
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Create new lecture failed!");
		}
	}

	return (
		<Layout>
			<GlobalSpacing>
				<NewCourseWrapper>
					<Typography variant="h2">
						{t("newCourse.title")}
					</Typography>

					<NewCourseContainer>
						<FormSection>
							<TextField fullWidth
								value={courseData.title}
								label={t("inputLabels.title")} name="title"
								onChange={onChangeInputValue("errorTitle")}
								error={courseData.errorTitle}
							/>

							<TextField fullWidth

								value={courseData.description}
								label={t("inputLabels.description")} name="description"
								onChange={onChangeInputValue("errorDesciption")}
								error={courseData.errorDesciption}
								multiline
								rows={4}
								// Ensure line breaks are preserved
							/>

							<TextField fullWidth
								value={courseData.price}
								label={t("inputLabels.price")} name="price" type="number"
								onChange={onChangeInputValue("errorPrice")}
								InputProps={{ startAdornment: "$" }}
								error={courseData.errorPrice}
								helperText={courseData.priceHelperText}
								className="base-filled"
							/>

							<Select value={courseData.lectureType}
								label={t("inputLabels.type")} name="lectureType"
								onChange={onChangeInputValue("errorLectureType")}
								error={courseData.errorLectureType}
								items={lectureTypes}
							/>

							<Select value={courseData.language}
								onChange={onChangeInputValue("errorLanguage")}
								label={t("inputLabels.language")} name="language"
								error={courseData.errorLanguage}
								items={languageLists}
							/>
						</FormSection>


						<RightContainer>
							<VideoUpload videoPreview={courseData.videoPreview}
								handleVideoUpload={handleVideoUpload}
								error={courseData.errorLecture}
							/>

							{/* learn Outcomes */}
							<AddItem ref={itemsRef}
								label={t("inputLabels.whatYouWillLearn")}
								onChange={onChangeAddItem}
								value={courseData.learnOutcomes}
							/>
						</RightContainer>
					</NewCourseContainer>

					<FirstButton onClick={handleSubmit}>
						<Typography variant="h6">{t("buttons.create")}</Typography>
					</FirstButton>
				</NewCourseWrapper>
			</GlobalSpacing>
		</Layout>
	)
}

const NewCourseWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-evenly",
	padding: `${theme.spacing(5)} 0px`,
	gap: theme.spacing(3),

	h2: {
		fontWeight: 500,
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		padding: `${theme.spacing(3)} 0px`,
	}
}))

const NewCourseContainer = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr 500px',
	gap: theme.spacing(2),

	[theme.breakpoints.down("lg")]: {
		display: "flex",
		flexDirection: "column-reverse",
	},
}))

const RightContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
}))

const FormSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
}))

export { NewCourse }